import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, ArrowRight, Play, Video, AlertCircle, Upload, X, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  HERO_HEADLINE,
  CTA_PRIMARY,
  CTA_UPLOAD,
  HOW_IT_WORKS_STEPS_GLOBAL,
  UPSELL_HEADLINE,
  UPSELL_TOOLS,
  ERROR_MESSAGES,
  MAX_UPLOAD_IMAGES,
  MAX_UPLOAD_FILE_SIZE_MB,
} from './constants';
import { createListingPreviewFromUpload, logListingEvent } from '../../services/listing-funnel';
import { detectAspectRatioFromImageFiles } from '../../lib/aspect-ratio';

type InputMode = 'upload';

function SortableThumb({ id, preview, index, onRemove }: { id: string; preview: string; index: number; onRemove: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 0,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative w-24 h-16 md:w-28 md:h-20 rounded-lg overflow-hidden border border-slate-700 bg-slate-800 group">
      <img src={preview} alt={`Image ${index + 1}`} className="w-full h-full object-cover" />
      <div {...attributes} {...listeners} className="absolute top-0 left-0 w-6 h-full flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab">
        <GripVertical size={12} className="text-white" />
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="absolute top-1 right-1 w-5 h-5 bg-red-600 hover:bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X size={12} className="text-white" />
      </button>
      <div className="absolute bottom-0 inset-x-0 bg-black/60 text-center py-0.5">
        <span className="text-[10px] text-white font-medium">{index + 1}</span>
      </div>
    </div>
  );
}

interface ListingLandingPageProps {
  onStartProcessing: (jobId: string, shareSlug: string, images: string[]) => void;
  onShowPreview: (shareSlug: string) => void;
}

const ListingLandingPage: React.FC<ListingLandingPageProps> = ({ onStartProcessing, onShowPreview }) => {
  const [mode] = useState<InputMode>('upload');
  const [error, setError] = useState<{ title: string; description: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);

  // Upload state — each item gets a stable ID for drag-and-drop
  const [uploadItems, setUploadItems] = useState<{ id: string; file: File; preview: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const selectedFiles = uploadItems.map((item) => item.file);
  const filePreviews = uploadItems.map((item) => item.preview);

  useEffect(() => {
    logListingEvent('landing_page_view');
  }, []);

  useEffect(() => {
    return () => {
      uploadItems.forEach((item) => URL.revokeObjectURL(item.preview));
    };
  }, [uploadItems]);

  const handleFilesSelected = (files: FileList | null) => {
    if (!files) return;
    setError(null);

    const newItems: { id: string; file: File; preview: string }[] = [];

    for (let i = 0; i < files.length && uploadItems.length + newItems.length < MAX_UPLOAD_IMAGES; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) continue;
      if (file.size > MAX_UPLOAD_FILE_SIZE_MB * 1024 * 1024) continue;
      newItems.push({
        id: `upload-${Date.now()}-${i}-${Math.random().toString(36).slice(2)}`,
        file,
        preview: URL.createObjectURL(file),
      });
    }

    setUploadItems((prev) => [...prev, ...newItems]);
  };

  const removeFile = (index: number) => {
    URL.revokeObjectURL(uploadItems[index].preview);
    setUploadItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setUploadItems((prev) => {
      const oldIndex = prev.findIndex((item) => item.id === active.id);
      const newIndex = prev.findIndex((item) => item.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleUploadSubmit = async () => {
    if (selectedFiles.length < 1) {
      setError(ERROR_MESSAGES.no_images);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setUploadStatus('Preparing...');
    logListingEvent('listing_url_submitted', { method: 'upload', imageCount: selectedFiles.length });

    try {
      const detectedAspectRatio = await detectAspectRatioFromImageFiles(selectedFiles);

      const result = await createListingPreviewFromUpload(
        selectedFiles,
        undefined,
        (uploaded, total) => {
          if (uploaded < total) {
            setUploadStatus(`Uploading image ${uploaded + 1} of ${total}...`);
          } else {
            setUploadStatus('Creating your project...');
          }
        },
        detectedAspectRatio
      );
      onStartProcessing(result.jobId, result.shareSlug, result.images);
    } catch (err: any) {
      const code = err.code ?? err.message ?? 'generic';
      const msg = ERROR_MESSAGES[code] ?? ERROR_MESSAGES.generic;
      setError(msg);
      if (code === 'rate_limited' && err.message) {
        setError({ title: msg.title, description: err.message });
      }
    } finally {
      setIsSubmitting(false);
      setUploadStatus(null);
    }
  };

  return (
    <main className="relative z-10">
      {/* Hero */}
      <section className="container mx-auto px-4 pt-20 pb-16">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-900/30 backdrop-blur-sm border border-blue-500/20 text-blue-300 rounded-full text-sm font-medium mb-8">
            <Sparkles size={14} className="shrink-0" />
            AI-Powered Property Video
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {HERO_HEADLINE}
          </h1>

          <p className="text-lg md:text-xl text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed">
            Upload up to 5 property images and ImageMotion will create a short AI preview video with cinematic motion, music and branding. No account needed.
          </p>

          {/* Upload */}
          {mode === 'upload' && (
            <div className="max-w-2xl mx-auto mb-4">
              {/* Drop zone */}
              <div
                className="relative mb-4 p-6 border-2 border-dashed border-slate-600 hover:border-blue-500/60 rounded-xl bg-slate-900/60 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFilesSelected(e.dataTransfer.files);
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFilesSelected(e.target.files)}
                />
                <div className="text-center">
                  <Upload size={32} className="mx-auto text-slate-500 mb-3" />
                  <p className="text-sm text-slate-300 font-medium">
                    Click to select or drag & drop up to {MAX_UPLOAD_IMAGES} images
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    JPG, PNG, WebP — max {MAX_UPLOAD_FILE_SIZE_MB}MB each
                  </p>
                </div>
              </div>

              {/* Draggable Thumbnails */}
              {uploadItems.length > 0 && (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={uploadItems.map((item) => item.id)} strategy={rectSortingStrategy}>
                    <div className="flex flex-wrap gap-2 mb-4 justify-center">
                      {uploadItems.map((item, i) => (
                        <SortableThumb
                          key={item.id}
                          id={item.id}
                          preview={item.preview}
                          index={i}
                          onRemove={() => removeFile(i)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}

              <button
                onClick={handleUploadSubmit}
                disabled={isSubmitting || selectedFiles.length < 1}
                className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold text-base transition-all shadow-xl shadow-blue-900/40 hover:shadow-blue-500/30"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {uploadStatus ?? 'Uploading & creating...'}
                  </>
                ) : (
                  <>
                    {CTA_UPLOAD} ({selectedFiles.length}/{MAX_UPLOAD_IMAGES})
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
              <p className="text-xs text-slate-500 mt-3">
                Upload property photos from any source — max {MAX_UPLOAD_FILE_SIZE_MB}MB each.
                {uploadItems.length > 1 ? ' Drag to reorder.' : ''}
              </p>
              <p className="text-[11px] text-slate-600 mt-2">
                Free preview — 1 per day. Create a free account for unlimited videos and downloads.
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="max-w-2xl mx-auto mb-6 p-4 bg-red-900/30 border border-red-500/30 rounded-xl text-left">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-300 font-medium">{error.title}</p>
                  <p className="text-red-400/80 text-sm mt-1">{error.description}</p>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* How it Works */}
      <section className="container mx-auto px-4 pb-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-12">
            How it works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {HOW_IT_WORKS_STEPS_GLOBAL.map((step) => (
              <div key={step.number} className="text-center">
                <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-blue-600/20 to-cyan-600/20 border border-blue-500/20 flex items-center justify-center">
                  <span className="text-xl font-bold text-blue-400">{step.number}</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Example Preview */}
      <section className="container mx-auto px-4 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-slate-900/60 border border-slate-700/60 rounded-2xl p-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Play size={20} className="text-blue-400" />
              <h3 className="text-lg font-semibold text-white">See what you get</h3>
            </div>
            <p className="text-slate-400 mb-6">
              A short AI-generated video with cinematic motion, branded intro/outro, and background music. Free preview with watermark.
            </p>
            <div className="aspect-video bg-slate-800/60 rounded-xl border border-slate-700/40 flex items-center justify-center">
              <div className="text-center">
                <Video size={48} className="text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Upload images above to get started</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ReHub Upsell */}
      <section className="container mx-auto px-4 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/40 border border-slate-700/60 rounded-2xl p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 text-center">
              {UPSELL_HEADLINE}
            </h2>
            <p className="text-slate-400 text-center mb-10 max-w-xl mx-auto">
              ImageMotion is part of the ReHub ecosystem for modern property marketing.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {UPSELL_TOOLS.map((tool) => (
                <a
                  key={tool.name}
                  href={tool.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-slate-800/40 border border-slate-700/40 hover:border-blue-500/30 hover:bg-slate-800/60 rounded-xl transition-all group"
                >
                  <div className="w-16 min-w-[4rem] h-10 rounded-lg bg-slate-800/60 flex items-center justify-center shrink-0 p-1.5">
                    <img src={tool.logo} alt="" className="max-h-full max-w-full w-auto object-contain" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-slate-200 group-hover:text-blue-300 transition-colors">{tool.name}</h3>
                    <p className="text-xs text-slate-400 line-clamp-2">{tool.description}</p>
                  </div>
                </a>
              ))}
            </div>
            <div className="text-center mt-8">
              <a
                href="https://hub.rehub.studio"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-slate-700/60 hover:bg-slate-700 border border-slate-600 rounded-xl text-sm font-medium text-white transition-colors"
              >
                Explore ReHub
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="container mx-auto px-4 pb-24">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to try it?
          </h2>
          <p className="text-slate-400 mb-8">
            Upload your property photos and get a preview video in under 2 minutes. Free, no signup required.
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-xl font-semibold text-base transition-all shadow-xl shadow-blue-900/40 hover:shadow-blue-500/30"
          >
            {CTA_PRIMARY}
            <ArrowRight size={18} />
          </button>
        </div>
      </section>
    </main>
  );
};

export default ListingLandingPage;
